/** Confirm before an index switch throws a basket away.
 *
 * Operator, 2026-08-29: *"On symbol change should clear"*, then *"Should
 * get confirmation dialog"*.
 *
 * The clearing itself is not optional — a NIFTY 24100 CE is not a strike
 * SENSEX lists at all, so a basket carried across indices is priced
 * against a ladder it was never built on. But losing a structure you spent
 * minutes assembling to one stray click on a dropdown is the kind of thing
 * a person only forgives once, and there is no undo here.
 *
 * ⚠️ Only asked when there is something to lose. A confirmation on an
 * empty basket is a dialog that always says yes, which teaches people to
 * dismiss the one that matters without reading it.
 */

import type { ReactElement } from "react";
import { Modal } from "@/components/ui/Modal";

interface Props {
  /** The index being switched TO, or null when nothing is pending. */
  pending: string | null;
  legCount: number;
  cancel: () => void;
  confirm: () => void;
}

export function SwitchIndexDialog({
  pending,
  legCount,
  cancel,
  confirm,
}: Readonly<Props>): ReactElement | null {
  if (pending === null) return null;
  return (
    <Modal
      open
      onClose={cancel}
      title={`Switch to ${pending}?`}
      // Says WHAT is lost, not just that something is. "2 positions" is
      // the fact that decides the answer.
      description={`This removes the ${String(legCount)} position${legCount === 1 ? "" : "s"} in your basket. ${pending}'s strikes are a different ladder, so they cannot be carried across.`}
      icon="ti-alert-triangle"
      iconTone="warning"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={cancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={confirm}>
            Switch and clear
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-secondary">
        Save the basket first if you want it back — it will still open on its own session.
      </p>
    </Modal>
  );
}
